if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mobileCounter: this.querySelector('[data-ts-gallery-current]'),
          previousButton: this.querySelector('[data-ts-gallery-prev]'),
          nextButton: this.querySelector('[data-ts-gallery-next]'),
        };
        this.viewerSlider = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        this.mql = window.matchMedia('(min-width: 750px)');
        this.preparedImages = new WeakSet();
        this.addEventListener('pointerover', this.prepareInteraction.bind(this));
        this.addEventListener('focusin', this.prepareInteraction.bind(this));
        this.addEventListener('pointerdown', this.prepareInteraction.bind(this), { passive: true });
        const firstImage = this.viewerSlider?.querySelector('.is-active img');
        if (firstImage?.complete) this.prepareAdjacentMedia();
        else firstImage?.addEventListener('load', () => this.prepareAdjacentMedia(), { once: true });
        if (!this.elements.thumbnails) return;

        this.viewerSlider?.addEventListener('scroll', this.onViewerScroll.bind(this), { passive: true });
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.elements.previousButton?.addEventListener('click', () => this.navigateGallery(-1));
        this.elements.nextButton?.addEventListener('click', () => this.navigateGallery(1));
        this.updateMobileCounter(this.elements.thumbnails.querySelector('[aria-current]')?.closest('[data-target]'));
        this.updateGalleryControls();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.viewerScrollFrame);
        clearTimeout(this.announcementTimer);
      }

      prepareImage(image) {
        if (!image || this.preparedImages.has(image)) return;
        this.preparedImages.add(image);
        image.decoding = 'async';
        image.loading = 'eager';
        image.decode?.().catch(() => {});
      }

      prepareAdjacentMedia() {
        if (!this.isConnected || !this.viewerSlider) return;
        const slides = Array.from(this.viewerSlider.children).filter((slide) => slide.matches('[data-media-id]'));
        const index = slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (index < 0) return;
        [index, (index + 1) % slides.length, (index - 1 + slides.length) % slides.length].forEach((position) => {
          this.prepareImage(slides[position].querySelector('.product__modal-opener--image img'));
        });
      }

      prepareInteraction(event) {
        const thumbnail = event.target.closest('[data-target]');
        if (thumbnail) {
          this.prepareImage(this.elements.viewer?.querySelector(
            `[data-media-id="${thumbnail.dataset.target}"] .product__modal-opener--image img`
          ));
        }
        const opener = event.target.closest('modal-opener');
        const modal = opener && document.querySelector(opener.dataset.modal);
        const mediaId = opener?.querySelector('[data-media-id]')?.dataset.mediaId;
        if (modal && mediaId) this.prepareImage(modal.querySelector(`img[data-media-id="${mediaId}"]`));
      }

      onViewerScroll() {
        if (this.mql.matches || !this.viewerSlider) return;

        if (this.viewerScrollFrame) return;
        this.viewerScrollFrame = requestAnimationFrame(() => {
          this.viewerScrollFrame = null;
          const slides = Array.from(this.viewerSlider.children).filter(
            (slide) => slide.matches('[data-media-id]') && slide.clientWidth > 0
          );
          if (slides.length < 2) return;

          const viewerRect = this.viewerSlider.getBoundingClientRect();
          const viewerCenter = viewerRect.left + viewerRect.width / 2;
          const activeMedia = slides.reduce((closest, slide) => {
            const slideRect = slide.getBoundingClientRect();
            const distance = Math.abs(slideRect.left + slideRect.width / 2 - viewerCenter);
            return !closest || distance < closest.distance ? { slide, distance } : closest;
          }, null)?.slide;
          if (!activeMedia) return;

          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
          if (!thumbnail || thumbnail.querySelector('[aria-current]')) return;

          slides.forEach((slide) => slide.classList.toggle('is-active', slide === activeMedia));
          this.setActiveThumbnail(thumbnail);
          this.prepareAdjacentMedia();
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.prepareImage(activeMedia.querySelector('.product__modal-opener--image img'));
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!this.mql.matches) {
          const slider = activeMedia.parentElement;
          const padding = parseFloat(getComputedStyle(slider).scrollPaddingLeft) || 0;
          slider.scrollTo({ left: activeMedia.offsetLeft - padding, behavior: 'instant' });
        } else if (!this.dataset.desktopLayout.includes('thumbnail')) {
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top < -0.5) {
            this.preventStickyHeader();
            window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
          }
        }
        this.playActiveMedia(activeMedia);
        this.prepareAdjacentMedia();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail || thumbnail.querySelector('[aria-current]')) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.updateMobileCounter(thumbnail);
        this.updateGalleryControls();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      updateMobileCounter(thumbnail) {
        if (!this.elements.mobileCounter || !thumbnail) return;
        this.elements.mobileCounter.textContent = thumbnail.dataset.mediaPosition || '1';
      }

      getGalleryItems() {
        if (!this.elements.thumbnails) return [];
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (item) => !item.classList.contains('thumbnail-list_item--variant')
        );
      }

      navigateGallery(direction) {
        const items = this.getGalleryItems();
        const activeIndex = items.findIndex((item) => item.querySelector('[aria-current]'));
        if (activeIndex < 0) return;

        if (items.length < 2) return;
        const nextIndex = (activeIndex + direction + items.length) % items.length;
        items[nextIndex].querySelector('button')?.click();
      }

      updateGalleryControls() {
        const items = this.getGalleryItems();
        const activeIndex = items.findIndex((item) => item.querySelector('[aria-current]'));
        if (activeIndex < 0) return;

        const shouldDisable = items.length < 2;
        if (this.elements.previousButton) this.elements.previousButton.disabled = shouldDisable;
        if (this.elements.nextButton) this.elements.nextButton.disabled = shouldDisable;
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        const announce = () => {
          if (!this.isConnected || !activeItem.classList.contains('is-active')) return;
          clearTimeout(this.announcementTimer);
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          this.announcementTimer = setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete && image.naturalWidth) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
